/**
 * Bank Connections React Query Hooks
 *
 * Provides data fetching and mutation hooks for bank connection and account operations.
 */

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createElement } from 'react';
import type { AccountUpdate, ExchangeRequest, ManualAccountCreate, SyncResponse } from '@/shared/types';
import { ToastAction } from '@/shared/components/ui/toast';
import { toast } from '@/shared/hooks/use-toast';
import { budgetKeys } from '@/features/budgets';
import { balanceKeys } from '@/features/settle-up';
import { transactionKeys } from '@/features/transactions/hooks';
import { bankingService } from '../services/banking-service';
import { balanceTrendKeys } from './use-balance-trends';
import { holdingKeys } from './use-holdings';
import { syncLogKeys } from './use-sync-logs';

// =============================================================================
// Query Key Factories
// =============================================================================

/**
 * Query key factory for bank connection queries
 */
export const bankConnectionKeys = {
  all: ['bank-connections'] as const,
  lists: () => [...bankConnectionKeys.all, 'list'] as const,
  detail: (id: string) => [...bankConnectionKeys.all, id] as const,
};

export interface AccountPerspectiveParams {
  user_id?: string;
  household?: boolean;
}

/**
 * Query key factory for account queries
 */
export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  listWithParams: (params?: AccountPerspectiveParams) => [...accountKeys.lists(), 'perspective', params] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch bank connections, optionally filtered by perspective
 *
 * @param params - Optional perspective filter (household or specific user)
 * @returns Query result with bank connections
 */
export function useBankConnections(params?: AccountPerspectiveParams) {
  return useQuery({
    queryKey: [...bankConnectionKeys.lists(), params],
    queryFn: () => bankingService.listConnections(params),
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch accounts, optionally filtered by perspective
 *
 * @param params - Optional perspective filter (household or specific user)
 * @returns Query result with accounts
 */
export function useAccounts(params?: AccountPerspectiveParams) {
  return useQuery({
    queryKey: accountKeys.listWithParams(params),
    queryFn: () => bankingService.listAccounts(params),
    placeholderData: keepPreviousData,
  });
}

// =============================================================================
// Mutation Hooks - Link Flow
// =============================================================================

/**
 * Hook to get a link token for initiating bank connection (Teller or Plaid).
 *
 * @returns Mutation for getting link token
 */
export function useLinkToken() {
  return useMutation({
    mutationFn: (params?: { provider?: string; products?: string[] }) =>
      bankingService.getLinkToken(params),
  });
}

/**
 * Hook to exchange Teller enrollment for a stored bank connection
 *
 * @returns Mutation for exchanging enrollment
 */
export function useExchangeEnrollment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExchangeRequest) => bankingService.exchangeEnrollment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
    },
  });
}

// =============================================================================
// Mutation Hooks - Connection Management
// =============================================================================

/**
 * Build a human-readable sync toast description from the response.
 */
function buildSyncDescription(data: SyncResponse): string {
  const parts: string[] = [];
  const { added, updated } = data.transactions;
  if (added > 0) parts.push(`${added} new transaction${added !== 1 ? 's' : ''}`);
  if (updated > 0) parts.push(`${updated} updated`);
  if (data.holdings_refreshed) parts.push('holdings refreshed');
  if (parts.length === 0) parts.push('No new changes');
  if (data.errors.length > 0) parts.push(`${data.errors.length} warning${data.errors.length !== 1 ? 's' : ''}`);
  return parts.join(' · ');
}

interface SyncConnectionOptions {
  onReconnectNeeded?: () => void;
  onFallback?: (provider: string) => void;
}

/**
 * Hook to sync a bank connection to fetch latest data
 *
 * @param options - Optional callbacks for reconnect/fallback toast actions
 * @returns Mutation for syncing a connection
 */
export function useSyncConnection(options?: SyncConnectionOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => bankingService.syncConnection(connectionId),
    onSuccess: (data: SyncResponse) => {
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: balanceTrendKeys.all });
      queryClient.invalidateQueries({ queryKey: syncLogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: holdingKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      toast({
        title: data.errors.length > 0 ? 'Sync completed with warnings' : 'Sync complete',
        description: buildSyncDescription(data),
        variant: data.errors.length > 0 ? 'destructive' : 'default',
      });
    },
    onError: (error: Error & { response?: { status?: number; data?: { error_code?: string } } }) => {
      const errorCode = error.response?.data?.error_code;
      const isAuthError = errorCode === 'bank_connection_expired' || errorCode === 'access_revoked';

      if (isAuthError) {
        const actions: React.ReactNode[] = [];
        if (options?.onReconnectNeeded) {
          actions.push(
            createElement(ToastAction, {
              key: 'reconnect',
              altText: 'Reconnect',
              onClick: options.onReconnectNeeded,
            }, 'Reconnect')
          );
        }
        if (options?.onFallback) {
          actions.push(
            createElement(ToastAction, {
              key: 'fallback',
              altText: 'Try Plaid',
              onClick: () => options.onFallback!('plaid'),
            }, 'Try Plaid')
          );
        }

        toast({
          title: 'Connection expired',
          description: 'Your bank connection needs to be re-linked.',
          variant: 'destructive',
          action: actions.length > 0 ? createElement('div', { className: 'flex gap-1' }, ...actions) : undefined,
        });
        return;
      }

      toast({
        title: 'Sync failed',
        description: error.message || 'Unable to sync. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to sync all active bank connections via server-side orchestration.
 *
 * Uses a single POST /banking/sync request instead of N parallel requests,
 * which avoids Teller rate limits by syncing connections sequentially
 * with delays on the server side.
 *
 * @returns Mutation for syncing all active connections
 */
export function useSyncAll() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => bankingService.syncAll(),
    onSuccess: (results) => {
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: balanceTrendKeys.all });
      queryClient.invalidateQueries({ queryKey: syncLogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: holdingKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      const totalAdded = results.reduce((sum, r) => sum + r.transactions.added, 0);
      const totalUpdated = results.reduce((sum, r) => sum + r.transactions.updated, 0);
      const holdingsRefreshed = results.some((r) => r.holdings_refreshed);
      const totalErrors = results.reduce((sum, r) => sum + r.errors.length, 0);

      const parts: string[] = [];
      if (totalAdded > 0) parts.push(`${totalAdded} new transaction${totalAdded !== 1 ? 's' : ''}`);
      if (totalUpdated > 0) parts.push(`${totalUpdated} updated`);
      if (holdingsRefreshed) parts.push('holdings refreshed');
      if (parts.length === 0) parts.push('No new changes');
      if (totalErrors > 0) parts.push(`${totalErrors} warning${totalErrors !== 1 ? 's' : ''}`);

      toast({
        title: totalErrors > 0 ? 'Sync completed with warnings' : `Synced ${results.length} connection${results.length !== 1 ? 's' : ''}`,
        description: parts.join(' · '),
        variant: totalErrors > 0 ? 'destructive' : 'default',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Sync failed',
        description: error.message || 'Unable to sync. Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to get an Update Mode link token for repairing a broken Plaid connection.
 * Returns the connectionId + linkToken needed to open Plaid Link in repair mode.
 */
export function useRepairConnection() {
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const tokenResponse = await bankingService.getUpdateLinkToken(connectionId);
      return { connectionId, linkToken: tokenResponse.link_token! };
    },
  });
}

/**
 * Hook to complete an Update Mode repair — clears error state and triggers sync.
 */
export function useCompleteRepair() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) => bankingService.completeUpdateMode(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: balanceTrendKeys.all });
      queryClient.invalidateQueries({ queryKey: syncLogKeys.lists() });
      queryClient.invalidateQueries({ queryKey: holdingKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/**
 * Hook to remove a bank connection
 *
 * @returns Mutation for removing a connection
 */
export function useRemoveConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: string) => bankingService.removeConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: balanceKeys.all });
      queryClient.invalidateQueries({ queryKey: balanceTrendKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// =============================================================================
// Mutation Hooks - Account Management
// =============================================================================

/**
 * Hook to update an account (e.g., rename or hide)
 *
 * @returns Mutation for updating an account
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: AccountUpdate }) =>
      bankingService.updateAccount(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

const systemAccountKeys = {
  all: ['system-account'] as const,
};

export function useSystemAccount() {
  return useQuery({
    queryKey: systemAccountKeys.all,
    queryFn: () => bankingService.getSystemAccount(),
  });
}

/**
 * Hook to delete an account (soft-delete with transaction reassignment for manual accounts)
 *
 * @returns Mutation for deleting an account
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (accountId: string) => bankingService.deleteAccount(accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.all });
      queryClient.invalidateQueries({ queryKey: transactionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: balanceKeys.all });
      queryClient.invalidateQueries({ queryKey: balanceTrendKeys.all });
      queryClient.invalidateQueries({ queryKey: budgetKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useCreateManualAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ManualAccountCreate) => bankingService.createManualAccount(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountKeys.all });
      queryClient.invalidateQueries({ queryKey: bankConnectionKeys.all });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
